import { Constants } from "@eagerdog/constants";
import { IShow } from "@eagerdog/interfaces";

/**
 * Builds a "class order" lookup for show types that have no fixed catalog
 * order (e.g. 4-H, which has no pre-defined level sort order the way AKC
 * does). Instead of a hardcoded array, this walks the Judge Schedule
 * (rings -> running_order -> dog_classes) in the order the classes were
 * actually placed/scheduled, and returns the ordinal position of each
 * distinct show_element/level/section combination the first time it's seen.
 *
 * When multiple shows (trials) of the same show_type exist in an event
 * (e.g. two 4-H Agility trials), all of their schedules are merged into a
 * single ordered list - shows are walked in array order and each class key
 * is assigned a position only the first time it appears across all of them.
 * This mirrors how the AKC arm number generators use a single fixed
 * catalog-order array regardless of which trial a class belongs to.
 */
export function getScheduleClassOrder(
  shows: Pick<IShow, "rings">[]
): Map<string, number> {
  const orderMap = new Map<string, number>();
  let nextIndex = 0;

  for (const show of shows) {
    for (const ring of show.rings || []) {
      for (const judgeEntry of (ring as any).running_order || []) {
        for (const dogClass of judgeEntry.dog_classes || []) {
          if (
            dogClass.type !== undefined &&
            dogClass.type !== Constants.running_order_dog_class_type.dog_class
          ) {
            continue;
          }

          const key = getScheduleClassOrderKey(
            dogClass.show_element,
            dogClass.level,
            dogClass.section
          );

          if (!orderMap.has(key)) {
            orderMap.set(key, nextIndex++);
          }
        }
      }
    }
  }

  return orderMap;
}

/**
 * Canonical key for schedule-class-order lookups - keep in sync between
 * building the map (getScheduleClassOrder) and looking values up.
 */
export function getScheduleClassOrderKey(
  showElement?: string,
  level?: string,
  section?: string
): string {
  return `${showElement || ""}|${level || ""}|${section || ""}`;
}
